import React from 'react';

import type { Component, ComponentType, LayoutChild } from '~/config/types';
import type { ConfiguredChild } from '~/hooks/use-config';
import { Animated } from './animated';
import { Comment } from './comment';
import { CommentForm } from './comment-form';
import { CommentReplyForm } from './comment-reply-form';
import { CommentThread } from './comment-thread';
import { Comments } from './comments';
import { CommentsLoadMore } from './comments-load-more';
import { Element, ElementWithSlot } from './element';
import { Header } from './header';
import { Lottie } from './lottie';
import { RadioInput } from './radio-input';
import { TextArea } from './text-area';
import { TextInput } from './text-input';

export function renderChild(child: ConfiguredChild, index?: number) {
  const indexStr = index !== undefined ? `-${index}` : '';
  const componentsMap: Record<ComponentType, React.FC<never>> = {
    Animated,
    Lottie,
    TextInput,
    TextArea,
    RadioInput,
    Header,
    Comments,
    CommentsLoadMore,
    Comment,
    CommentThread,
    CommentForm,
    CommentReplyForm,
  };

  if (isComponent(child)) {
    const children = Array.isArray(child.children)
      ? child.children.map((c, i) => {
          return renderChild(c as ConfiguredChild, i);
        })
      : child.children;

    const componentProps = { ...child.componentProps, children };
    const Component = componentsMap[child.component] as React.ComponentType<React.PropsWithChildren>;

    return <Component key={`${child.component}${indexStr}`} {...componentProps} />;
  }

  const { children } = child;
  const elementProps = {
    ...child.elementProps,
    ...(children && {
      children: Array.isArray(children)
        ? children.map((child, index) => {
            return renderChild(child, index);
          })
        : children,
    }),
  };

  return child.elementProps.slot ? (
    <ElementWithSlot key={`${child.elementProps.tag}${indexStr}`} {...elementProps} />
  ) : (
    <Element key={`${child.elementProps.tag}${indexStr}`} {...elementProps} />
  );
}

function isComponent(child: LayoutChild): child is Component {
  return 'component' in child;
}
